import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import { toNoZWireDate } from '@/settings/components/practice-edit/practice-edit-merge';
import type {
  StaffNursingSchoolRow,
  StaffProfessionalDetails,
} from '@/types/settings-api';

/**
 * Staff Professional Details — PROD-788 (nurse-only tab).
 *
 * Two endpoints under one wrapper:
 *   - GET  /api/Staff/ProfessionalDetails?staffId={id}
 *   - POST /api/Staff/ProfessionalDetails (body = same wrapper shape)
 *
 * **DO NOT confuse** with the Practitioner variant
 * `useProfessionalDetails`. Different path (`/api/Staff/...`), different
 * wrapper (nested `{staffId, professionalDetails, nursingSchool, deletedIds}`),
 * different ID-parameter mode (query, not path), different field-name
 * spelling (American not British). Wire shapes verified against captured
 * dev cURLs 2026-05-24/25 — see `docs/CONVERSIONS/settings-staff-
 * professional-details/DEV_CURL.md` for the full contract.
 *
 * The POST is a **DIFF, not a PUT** — see `buildStaffProfessionalSaveBody`
 * below for the per-row dirty-flag + deleted-ids mapper.
 */

export const staffProfessionalDetailsQueryKey = (
  staffId: string | null | undefined,
) => ['settings', 'staff-professional-details', staffId] as const;

/**
 * Fetch the nested wrapper for a staff member.
 *
 * Backend returns 200 + the full wrapper even for staff with no prior
 * professional-details row yet (`professionalDetails.id: null`,
 * `nursingSchool: []`). No 404 path — the wrapper always exists.
 */
export function useStaffProfessionalDetails(
  staffId: string | null | undefined,
) {
  return useQuery({
    queryKey: staffProfessionalDetailsQueryKey(staffId),
    queryFn: ({ signal }) => {
      if (!staffId) {
        throw new Error('useStaffProfessionalDetails called without staffId');
      }
      const qs = new URLSearchParams({ staffId });
      return get<StaffProfessionalDetails>(
        `/api/Staff/ProfessionalDetails?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!staffId,
  });
}

/**
 * POST the diff. **Caller assembles the body via `buildStaffProfessionalSaveBody`;
 * this hook is pure transport.** Separation-of-concerns pattern locked in
 * Samer review on PR #323 (Create Doctor) — keeps the wire-shape rules out
 * of every call site.
 *
 * Cache invalidation auto-fires on success against the GET key so the form
 * refetches the server-canonical wrapper (with fresh row ids on new rows
 * + `deletedIds` reset to `[]`).
 */
export function useSaveStaffProfessionalDetails(
  staffId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StaffProfessionalDetails) =>
      post<StaffProfessionalDetails>('/api/Staff/ProfessionalDetails', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: staffProfessionalDetailsQueryKey(staffId),
      });
    },
  });
}

// === Save-body mapper (the diff/upsert contract) ===

export interface BuildStaffProfessionalSaveBodyInput {
  /** Echo of the route param — required by the wrapper. */
  staffId: string;
  /** Original GET wrapper (snapshot before edits) — used to compute the diff. */
  source: StaffProfessionalDetails;
  /** Edited wrapper (current React draft). */
  draft: StaffProfessionalDetails;
}

/**
 * Compose the POST body from the original GET + the current React draft.
 *
 * Behaviour per the captured dev curl (DEV_CURL.md §5):
 *
 * **`professionalDetails` block**
 *   - If the block was dirtied → ship the original `id` (or `null` on first
 *     save) + `isUpdated: true`.
 *   - If untouched → ship the original block verbatim with `isUpdated: false`
 *     (backend skips it).
 *
 * **`nursingSchool` rows**
 *   - NEW row (no `id` in source, in draft): `id: null`, `isUpdated: false`.
 *     Backend appends.
 *   - EXISTING row, touched (id in both, content differs): include with
 *     original `id` + `isUpdated: true`. Backend updates.
 *   - EXISTING row, untouched (id matches + content equal): **OMIT** from
 *     `nursingSchool[]`. The wire is APPEND-style; omission = preserve.
 *
 * **`deletedIds[]`**
 *   - Any source row id NOT present in draft `nursingSchool[]` gets pushed
 *     to `deletedIds[]`. Backend drops those rows.
 *
 * **`code` envelope**
 *   - Always ship `description: null, shortName: null` (master endpoints
 *     omit them; .NET nullable-field rule requires they be present).
 *     `StaffSpecialityCode` already has all 5 fields, but `useDegreeSearch`
 *     responses come with only 3 — we backfill the two nulls here.
 *
 * **`licenseExpirationDate`**
 *   - Strip trailing `Z` (CLAUDE.md API-contract rule). React forms emit
 *     `…Z` from `<input type="date">` in some flows; the .NET model-binder
 *     reads no-Z form.
 */
export function buildStaffProfessionalSaveBody(
  input: BuildStaffProfessionalSaveBodyInput,
): StaffProfessionalDetails {
  const { staffId, source, draft } = input;

  // ── professionalDetails block ────────────────────────────────────────
  const sourceBlock = source.professionalDetails;
  const draftBlock = draft.professionalDetails;
  const blockTouched =
    sourceBlock.licenseNumber !== draftBlock.licenseNumber ||
    sourceBlock.licenseIssuingAuthority !== draftBlock.licenseIssuingAuthority ||
    sourceBlock.licenseExpirationDate !== draftBlock.licenseExpirationDate;

  const professionalDetails = {
    id: sourceBlock.id,
    licenseNumber: draftBlock.licenseNumber,
    licenseIssuingAuthority: draftBlock.licenseIssuingAuthority,
    // PROD-1778: was a module-private `stripTrailingZ()` doing
    // `iso.slice(0, -1)` — a duplicate of the same false safeguard fixed in
    // practice-edit-merge.ts. Chopping the `Z` off a `toISOString()` result
    // relabels a UTC wall-clock as local instead of converting it. This field
    // is a calendar day, so use the shared day-preserving normaliser.
    licenseExpirationDate: toNoZWireDate(draftBlock.licenseExpirationDate),
    isUpdated: blockTouched,
  };

  // ── nursingSchool[] rows (diff against source) ───────────────────────
  const sourceById = new Map(
    source.nursingSchool
      .filter((r): r is StaffNursingSchoolRow & { id: string } => !!r.id)
      .map((r) => [r.id, r]),
  );

  const nursingSchoolOut: StaffNursingSchoolRow[] = [];
  const draftIds = new Set<string>();

  for (const row of draft.nursingSchool) {
    if (row.id === null) {
      // NEW row — backend will append.
      nursingSchoolOut.push(normaliseRow(row, false));
      continue;
    }
    draftIds.add(row.id);
    const originalRow = sourceById.get(row.id);
    if (!originalRow) {
      // Defensive: id present in draft but not in source. Treat as new.
      nursingSchoolOut.push(normaliseRow({ ...row, id: null }, false));
      continue;
    }
    if (isRowEqual(originalRow, row)) {
      // Untouched — OMIT from nursingSchool[] (append-style wire).
      continue;
    }
    // Touched existing row.
    nursingSchoolOut.push(normaliseRow(row, true));
  }

  // ── deletedIds[] ─────────────────────────────────────────────────────
  const deletedIds: string[] = [];
  for (const id of sourceById.keys()) {
    if (!draftIds.has(id)) deletedIds.push(id);
  }

  return {
    staffId,
    professionalDetails,
    nursingSchool: nursingSchoolOut,
    deletedIds,
  };
}

/** Equality check for nursing-school rows — used to detect untouched rows. */
function isRowEqual(
  a: StaffNursingSchoolRow,
  b: StaffNursingSchoolRow,
): boolean {
  return (
    a.university.Id === b.university.Id &&
    a.degree.degree === b.degree.degree &&
    a.passingYear === b.passingYear
  );
}

/**
 * Normalise a row for POST — backfills the two nullable `code` fields.
 *
 * **DO NOT STRIP `description: null` OR `shortName: null` AS "DEAD FIELDS".**
 *
 * The two master typeahead endpoints (`useDegreeSearch`,
 * `useMedicalSchoolsSearch`) return a 3-field `code` envelope
 * (`code` / `displayName` / `system`). The .NET POST handler at
 * `/api/Staff/ProfessionalDetails` reads the full 5-field shape and
 * rejects the body if `description` or `shortName` are missing entirely.
 * Backfilling explicit nulls is the contract — captured 2026-05-25 in
 * `docs/CONVERSIONS/settings-staff-professional-details/DEV_CURL.md` §5
 * (Findings 5 + 7). The unit test
 * `'backfills description + shortName nulls on new rows'` asserts these
 * are present in the output.
 */
function normaliseRow(
  row: StaffNursingSchoolRow,
  isUpdated: boolean,
): StaffNursingSchoolRow {
  return {
    id: row.id,
    university: row.university,
    degree: {
      degree: row.degree.degree,
      code: {
        code: row.degree.code.code,
        displayName: row.degree.code.displayName,
        system: row.degree.code.system,
        description: row.degree.code.description ?? null,
        shortName: row.degree.code.shortName ?? null,
      },
    },
    passingYear: row.passingYear,
    isUpdated,
  };
}

// ===========================================================================
// PA-ONLY — restored after the PROD-1817 port replaced this file with PM's.
//
// PM has no equivalent: these helpers came from PROD-1206 (Codex review) and
// encode a real fix. The copy silently dropped them, which is exactly the class
// of loss the port has to watch for — PM being the design reference does not
// mean PM is a superset of PA's logic.
// ===========================================================================

/**
 * Insert-or-replace a nursing-school row by EDIT POSITION (PROD-1206 Codex
 * fix). `editingIndex === null` = add mode → append. Otherwise replace the row
 * at that index — correct even for a freshly-added, unsaved row whose `id` is
 * still `null` (keying the replace on `id` made editing such a row append a
 * duplicate, since every unsaved row shares `id === null`).
 */
export function upsertNursingSchoolRow(
  rows: StaffNursingSchoolRow[],
  row: StaffNursingSchoolRow,
  editingIndex: number | null,
): StaffNursingSchoolRow[] {
  if (editingIndex === null) return [...rows, row];
  return rows.map((r, i) => (i === editingIndex ? row : r));
}

/** Remove a nursing-school row by edit position (companion to the upsert). */
export function removeNursingSchoolRow(
  rows: StaffNursingSchoolRow[],
  editingIndex: number | null,
): StaffNursingSchoolRow[] {
  if (editingIndex === null) return rows;
  return rows.filter((_, i) => i !== editingIndex);
}
